import { useEffect, useMemo, useState } from 'react';
import {
  buildQueueEmailPeerCounts,
  completenessFieldGroupsFromStats,
  groupStagingReviewItems,
  isInFileDuplicateDoc,
  isNamesOnlyStagingDoc,
  reasonLabel,
  STAGING_DUPLICATE_CATEGORY,
  STAGING_EXISTING_MATCH_CATEGORY,
  STAGING_NAMES_ONLY_CATEGORY,
} from '../utils/stagingReviewCategories';

/**
 * Left nav: in-file duplicates + existing SQL matches + per-field completeness counts.
 */
export default function StagingReviewQueue({
  items,
  selectedId,
  onSelect,
  loading,
  completenessStats = null,
  completenessItems = [],
  selectedFieldKey = null,
  onSelectField,
  completenessLoading = false,
}) {
  const { duplicateGroup, existingMatchGroup, namesOnlyGroup, duplicateEmailGroups, peerCounts } =
    useMemo(() => {
    const peerCounts = buildQueueEmailPeerCounts(items);
    const grouped = groupStagingReviewItems(items);
    const dupItems = grouped.find((g) => g.id === 'duplicate_or_match')?.items || [];
    const existingMatchGroup = {
      ...STAGING_EXISTING_MATCH_CATEGORY,
      items: grouped.find((g) => g.id === 'existing_match')?.items || [],
    };
    const namesOnlyGroup = {
      ...STAGING_NAMES_ONLY_CATEGORY,
      items: grouped.find((g) => g.id === 'missing_email')?.items || [],
    };

    const byEmail = new Map();
    for (const doc of dupItems) {
      const key =
        String(doc.emailKey || doc.ruleResult?.email || '')
          .trim()
          .toLowerCase() || `(no-email:${doc.id})`;
      if (!byEmail.has(key)) byEmail.set(key, []);
      byEmail.get(key).push(doc);
    }
    const duplicateEmailGroups = Array.from(byEmail.entries()).map(([email, docs]) => ({
      email,
      docs,
    }));

    const duplicateGroup = {
      ...STAGING_DUPLICATE_CATEGORY,
      items: dupItems,
    };

    return { duplicateGroup, existingMatchGroup, namesOnlyGroup, duplicateEmailGroups, peerCounts };
  }, [items]);

  const fieldGroups = useMemo(
    () => completenessFieldGroupsFromStats(completenessStats),
    [completenessStats]
  );

  const [dupOpen, setDupOpen] = useState(true);
  const [existingOpen, setExistingOpen] = useState(true);
  const [namesOnlyOpen, setNamesOnlyOpen] = useState(true);
  const [openCategories, setOpenCategories] = useState(() =>
    Object.fromEntries(fieldGroups.map((g) => [g.category, true]))
  );

  useEffect(() => {
    setOpenCategories((prev) => {
      const next = { ...prev };
      for (const g of fieldGroups) {
        if (next[g.category] === undefined) next[g.category] = true;
      }
      return next;
    });
  }, [fieldGroups]);

  if (loading && !items?.length && !completenessStats) {
    return <p className="text-sm theme-text-secondary">Loading…</p>;
  }

  const renderDocList = (docs, emptyLabel) => (
    <ul className="space-y-1 p-2 border-t">
      {docs.length === 0 ? (
        <li className="text-xs theme-text-secondary px-2 py-1">{emptyLabel}</li>
      ) : (
        docs.map((doc) => (
          <li key={doc.id}>
            <QueueRow
              doc={doc}
              peerCounts={peerCounts}
              selected={selectedId === doc.id && !selectedFieldKey}
              onSelect={() => {
                onSelectField?.(null);
                onSelect(doc.id);
              }}
            />
          </li>
        ))
      )}
    </ul>
  );

  return (
    <div className="space-y-3">
      <div className="border rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setDupOpen((v) => !v)}
          className="w-full flex items-start justify-between gap-2 px-3 py-2 text-left theme-text bg-[var(--staff-surface,#f8fafc)]"
          aria-expanded={dupOpen}
        >
          <span className="min-w-0">
            <span className="font-semibold text-sm block">
              {dupOpen ? '▾' : '▸'} {STAGING_DUPLICATE_CATEGORY.label}
            </span>
            <span className="text-xs theme-text-secondary block mt-0.5">
              {STAGING_DUPLICATE_CATEGORY.description}
            </span>
          </span>
          <span className="text-xs font-semibold shrink-0 px-2 py-0.5 rounded-full border">
            {duplicateGroup.items.length}
          </span>
        </button>
        {dupOpen && (
          <div className="border-t p-2 space-y-2">
            {duplicateEmailGroups.length === 0 ? (
              <p className="text-xs theme-text-secondary px-2 py-1">
                No duplicate emails in this upload
              </p>
            ) : (
              duplicateEmailGroups.map((group) => (
                <div key={group.email} className="rounded border bg-white/70">
                  <div className="px-2 py-1.5 text-xs font-semibold theme-text flex items-center justify-between gap-2">
                    <span className="truncate break-all">{group.email}</span>
                    <span className="shrink-0 px-1.5 py-0.5 rounded-full border text-[10px]">
                      {group.docs.length} rows
                    </span>
                  </div>
                  <ul className="space-y-1 p-1.5 pt-0">
                    {group.docs.map((doc, idx) => (
                      <li key={doc.id}>
                        <QueueRow
                          doc={doc}
                          peerCounts={peerCounts}
                          selected={selectedId === doc.id && !selectedFieldKey}
                          onSelect={() => {
                            onSelectField?.(null);
                            onSelect(doc.id);
                          }}
                          subtitle={`Row ${idx + 1}/${group.docs.length} · ${doc.status}${
                            doc.externalKey ? ` · ${doc.externalKey}` : ''
                          }`}
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <div className="border rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setNamesOnlyOpen((v) => !v)}
          className="w-full flex items-start justify-between gap-2 px-3 py-2 text-left theme-text bg-[var(--staff-surface,#f8fafc)]"
          aria-expanded={namesOnlyOpen}
        >
          <span className="min-w-0">
            <span className="font-semibold text-sm block">
              {namesOnlyOpen ? '▾' : '▸'} {STAGING_NAMES_ONLY_CATEGORY.label}
            </span>
            <span className="text-xs theme-text-secondary block mt-0.5">
              {STAGING_NAMES_ONLY_CATEGORY.description}
            </span>
          </span>
          <span className="text-xs font-semibold shrink-0 px-2 py-0.5 rounded-full border">
            {namesOnlyGroup.items.length}
          </span>
        </button>
        {namesOnlyOpen &&
          renderDocList(namesOnlyGroup.items, 'No name-only rows in this upload')}
      </div>

      <div className="border rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setExistingOpen((v) => !v)}
          className="w-full flex items-start justify-between gap-2 px-3 py-2 text-left theme-text bg-[var(--staff-surface,#f8fafc)]"
          aria-expanded={existingOpen}
        >
          <span className="min-w-0">
            <span className="font-semibold text-sm block">
              {existingOpen ? '▾' : '▸'} {STAGING_EXISTING_MATCH_CATEGORY.label}
            </span>
            <span className="text-xs theme-text-secondary block mt-0.5">
              {STAGING_EXISTING_MATCH_CATEGORY.description}
            </span>
          </span>
          <span className="text-xs font-semibold shrink-0 px-2 py-0.5 rounded-full border">
            {existingMatchGroup.items.length}
          </span>
        </button>
        {existingOpen &&
          renderDocList(existingMatchGroup.items, 'No existing-student matches in queue')}
      </div>

      {fieldGroups.map((group) => {
        const open = openCategories[group.category] !== false;
        return (
          <div key={group.category} className="border rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() =>
                setOpenCategories((prev) => ({
                  ...prev,
                  [group.category]: !open,
                }))
              }
              className="w-full flex items-start justify-between gap-2 px-3 py-2 text-left theme-text bg-[var(--staff-surface,#f8fafc)]"
              aria-expanded={open}
            >
              <span className="min-w-0">
                <span className="font-semibold text-sm block">
                  {open ? '▾' : '▸'} {group.label}
                </span>
                <span className="text-xs theme-text-secondary block mt-0.5">
                  Same missing-field counts as Student profile completeness
                </span>
              </span>
              <span className="text-xs font-semibold shrink-0 px-2 py-0.5 rounded-full border">
                {group.missingStudentCount}
              </span>
            </button>
            {open && (
              <div className="border-t p-2 space-y-1">
                {group.fields.map((field) => {
                  const active = selectedFieldKey === field.key;
                  return (
                    <div key={field.key}>
                      <button
                        type="button"
                        onClick={() => onSelectField?.(active ? null : field.key)}
                        className={`w-full flex items-center justify-between gap-2 text-left px-2 py-1.5 rounded text-sm border ${
                          active ? 'theme-btn-primary' : 'theme-card theme-text'
                        }`}
                      >
                        <span className="truncate">{field.label}</span>
                        <span className="text-xs font-semibold shrink-0">{field.missingCount}</span>
                      </button>
                      {active && (
                        <ul className="mt-1 ml-2 space-y-1">
                          {completenessLoading ? (
                            <li className="text-xs theme-text-secondary px-2 py-1">Loading students…</li>
                          ) : completenessItems.length === 0 ? (
                            <li className="text-xs theme-text-secondary px-2 py-1">
                              No students missing this field
                            </li>
                          ) : (
                            completenessItems.map((doc) => (
                              <li key={doc.id}>
                                <QueueRow
                                  doc={doc}
                                  peerCounts={peerCounts}
                                  selected={selectedId === doc.id}
                                  onSelect={() => onSelect(doc.id)}
                                  subtitle={
                                    (doc.ruleResult?.reviewReasons || []).slice(0, 2).join(', ') ||
                                    'incomplete'
                                  }
                                />
                              </li>
                            ))
                          )}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function queueDisplayName(doc) {
  if (isNamesOnlyStagingDoc(doc)) {
    const name = [doc.ruleResult?.firstName, doc.ruleResult?.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    if (name) return name;
  }
  return doc.emailKey || doc.ruleResult?.email || '(no email)';
}

function QueueRow({ doc, selected, onSelect, subtitle, peerCounts }) {
  const reasons = doc.ruleResult?.reviewReasons || [];
  const conflicts = doc.ruleResult?.conflicts || [];
  const isDup = isInFileDuplicateDoc(doc, peerCounts);
  const namesOnly = isNamesOnlyStagingDoc(doc);
  const emailKey = String(doc.emailKey || doc.ruleResult?.email || '')
    .trim()
    .toLowerCase();
  const occurrence = Math.max(
    Number(doc.ruleResult?.emailOccurrenceCount) || 0,
    peerCounts?.get?.(emailKey) || 0
  );
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left p-2 rounded text-sm border ${
        selected ? 'theme-btn-primary' : 'theme-card theme-text'
      }`}
    >
      <div className="font-medium truncate">
        {queueDisplayName(doc)}
        {namesOnly && (
          <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border border-slate-300 text-slate-800 bg-slate-50">
            name only
          </span>
        )}
        {occurrence > 1 && (
          <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border border-amber-300 text-amber-900 bg-amber-50">
            ×{occurrence} in file
          </span>
        )}
        {isDup && (
          <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border border-indigo-300 text-indigo-800 bg-indigo-50">
            merge / enroll
          </span>
        )}
      </div>
      <div className="opacity-80 text-xs mt-0.5">
        {subtitle ||
          [doc.status, ...reasons.slice(0, 2).map(reasonLabel), ...conflicts.slice(0, 1)]
            .filter(Boolean)
            .join(' · ') ||
          'review'}
      </div>
    </button>
  );
}
